import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { palette, spaces } from '../../../../assets/styles/themes/tokens';
import { currencyFormat } from '../../../../utils/format';
import { deriveBilling, toPerMonth } from '../checkout.helpers';
import { TERM_MONTHS } from '../checkout.constants';
import type { StripeCheckout } from '../checkout.types';
import { PriceSummarySkeleton } from './CheckoutSkeletons';

const OrderTotalSummary: React.FC<{ checkout: StripeCheckout | null }> = ({ checkout }) => {
  const { t } = useTranslation();

  // No session yet (initial load / warming) → skeleton.
  if (!checkout) return <PriceSummarySkeleton />;

  const { trial, isYearly, billing } = deriveBilling(checkout);

  // LicenceInfo shows a headline monthly price. `minorUnitsAmount` is in cents.
  const monthlyPriceLabel = currencyFormat(
    toPerMonth(billing.total.minorUnitsAmount, isYearly) / 100,
  );

  return (
    <Box
      data-testid='stripe-checkout-order-total'
      sx={{ display: 'flex', flexDirection: 'column', gap: spaces.sm }}
    >
      <Typography variant='body2' sx={{ color: palette.text.primary }}>
        {trial ? t('After your test') : t('Your chosen package')}
      </Typography>
      <Box>
        <Typography
          variant='h2'
          sx={{ color: palette.text.primary }}
          data-testid='stripe-checkout-monthly-price'
        >
          {t('{{amount}} monthly', { amount: monthlyPriceLabel })}
        </Typography>
        <Typography
          variant='caption'
          sx={{ color: palette.text.primary }}
          data-testid='stripe-checkout-duration'
        >
          {t('<termLength> months duration', { termLength: TERM_MONTHS })}
        </Typography>
      </Box>
    </Box>
  );
};

export default OrderTotalSummary;
